import { AuditActionPrefix, AuditActorType, type AuditLogEntry } from '@bartizo/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Fragment, useId, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { listAuditLog, type AuditLogFilters } from '../api/audit-log.js';
import { useCurrentUser } from '../auth/session-context.js';
import { EmptyState } from '../components/EmptyState.js';
import { PageHeader } from '../components/PageHeader.js';
import { describeValue, diffFields, type FieldChange } from '../lib/audit-diff.js';
import { effectiveTimezone, formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { DataTable, Facts } from '../ui/DataTable.js';
import { Dialog } from '../ui/Dialog.js';
import { SelectField, TextField } from '../ui/Field.js';

/**
 * The audit log (knowledge doc sections 11, 15 and 17; P8.3): every settings
 * change and action, who made it, and what it was before and after.
 *
 * Admins only, here and in the API. Filters live in the URL, as on the events
 * page, so a server's page can link to its own history and an admin can send a
 * colleague "everything done to settings last week".
 */

const ALL = '__all__';

const RANGES = [
  { value: '168', label: 'Last week' },
  { value: '720', label: 'Last 30 days' },
  { value: '8760', label: 'Last year' },
  { value: 'all', label: 'All time' },
] as const;

/** Suggestions for the action box; the API takes any prefix. */
const KNOWN_PREFIXES = [
  'action.',
  'enrollment-token.',
  'event.',
  'server.',
  'server-group.',
  'settings.',
  'setup.',
  'user.',
  'web_environment.',
  'web_rules.',
] as const;

const WHO: Record<AuditActorType, string> = {
  user: 'People',
  agent: 'Agents',
  system: 'Bartizo itself',
};

export function AuditLogPage(): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const actionListId = useId();
  const [open, setOpen] = useState<AuditLogEntry | null>(null);

  const [params, setParams] = useSearchParams();
  const [history, setHistory] = useState<(string | undefined)[]>([undefined]);
  const cursor = history[history.length - 1];
  const read = (key: string): string => params.get(key) ?? '';
  const hours = params.get('hours') ?? '720';

  const set = (key: string, value: string): void => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value === '' || value === ALL) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
    setHistory([undefined]);
  };

  // Pinned when the range changes, for the reason the events page gives: a `from`
  // recomputed each render never settles, and a sliding window pages unevenly.
  const from = useMemo(
    () =>
      hours === 'all' ? undefined : new Date(Date.now() - Number(hours) * 3_600_000).toISOString(),
    [hours],
  );

  const actorType = AuditActorType.safeParse(read('actor_type'));
  // Checked here as the API checks it, so a stray character is a note under the
  // box rather than a failed page; until it is fixed the filter is left out.
  const action = AuditActionPrefix.safeParse(read('action').toLowerCase());
  const actionError =
    read('action') !== '' && !action.success
      ? 'Only lower-case letters, digits, “.”, “_” and “-”.'
      : null;
  const filters: AuditLogFilters = {
    ...(actorType.success ? { actor_type: actorType.data } : {}),
    ...(read('actor_id') === '' ? {} : { actor_id: read('actor_id') }),
    ...(action.success ? { action: action.data } : {}),
    ...(read('target') === '' ? {} : { target: read('target') }),
    ...(from === undefined ? {} : { from }),
    ...(cursor === undefined ? {} : { cursor }),
  };

  const entries = useQuery({
    queryKey: ['audit-log', filters],
    queryFn: () => listAuditLog(filters),
    placeholderData: keepPreviousData,
  });

  const target = read('target');
  const actorId = read('actor_id');
  const anyFilter = ['actor_type', 'actor_id', 'action', 'target'].some((key) => read(key) !== '');

  return (
    <div className="page">
      <PageHeader
        title="Audit log"
        description={
          target === '' && actorId === '' ? (
            'Every settings change and action: who, when, and what it was before and after.'
          ) : (
            <>
              {target !== '' && (
                <>
                  Done to <code>{target}</code>.{' '}
                </>
              )}
              {actorId !== '' && (
                <>
                  Done by <code>{actorId}</code>.{' '}
                </>
              )}
              <Link to="/audit-log">Show everything</Link>.
            </>
          )
        }
      />

      <div className="filters">
        <SelectField
          label="Who"
          value={actorType.success ? actorType.data : ALL}
          onChange={(event) => {
            set('actor_type', event.target.value);
          }}
        >
          <option value={ALL}>Anyone</option>
          {AuditActorType.options.map((option) => (
            <option key={option} value={option}>
              {WHO[option]}
            </option>
          ))}
        </SelectField>

        <TextField
          label="Action"
          hint="The start of one: server. is everything done to servers."
          error={actionError}
          type="search"
          list={actionListId}
          placeholder="settings."
          value={read('action')}
          onChange={(event) => {
            set('action', event.target.value);
          }}
        />
        <datalist id={actionListId}>
          {KNOWN_PREFIXES.map((prefix) => (
            <option key={prefix} value={prefix} />
          ))}
        </datalist>

        <SelectField
          label="Time"
          value={hours}
          onChange={(event) => {
            set('hours', event.target.value);
          }}
        >
          {RANGES.map((range) => (
            <option key={range.value} value={range.value}>
              {range.label}
            </option>
          ))}
        </SelectField>
      </div>

      {entries.isError && (
        <Alert tone="danger" title="Could not load the audit log">
          {entries.error instanceof Error ? entries.error.message : 'Try again in a moment.'}
        </Alert>
      )}

      {entries.isPending && (
        <p className="row muted" aria-busy="true">
          <span className="spinner" aria-hidden="true" />
          Loading the audit log…
        </p>
      )}

      {entries.data?.items.length === 0 && (
        <EmptyState title="Nothing matches">
          {anyFilter
            ? 'Try widening the filters, or looking further back.'
            : 'Nothing has been changed or done in this period.'}
        </EmptyState>
      )}

      {entries.data !== undefined && entries.data.items.length > 0 && (
        <>
          <DataTable
            caption="Audit log entries, newest first, with who made each and what it changed"
            columns={[
              {
                key: 'when',
                header: 'When',
                sortValue: (entry) => Date.parse(entry.createdAt),
                cell: (entry) => (
                  <span title={formatInstant(entry.createdAt, { timezone })}>
                    {formatRelative(entry.createdAt)}
                  </span>
                ),
              },
              { key: 'who', header: 'Who', cell: (entry) => <Actor entry={entry} /> },
              {
                key: 'what',
                header: 'What',
                rowHeader: true,
                sortValue: (entry) => entry.action,
                cell: (entry) => <code>{entry.action}</code>,
              },
              {
                key: 'on',
                header: 'On',
                wideOnly: true,
                cell: (entry) => <Target entry={entry} />,
              },
              {
                key: 'changes',
                header: 'Changes',
                align: 'end',
                cell: (entry) => {
                  const n = diffFields(entry.before, entry.after).length;
                  return n === 0 ? <span className="muted">—</span> : n;
                },
              },
            ]}
            rows={entries.data.items}
            rowKey={(entry) => entry.id}
            onSelect={setOpen}
            selectedKey={open?.id}
          />
          {open !== null && (
            <Dialog
              variant="panel"
              eyebrow="Audit log"
              title={open.action}
              description={`${formatInstant(open.createdAt, { timezone })}${open.ip === null ? '' : ` · from ${open.ip}`}`}
              onClose={() => {
                setOpen(null);
              }}
            >
              <Facts
                items={[
                  ['Who', <Actor key="who" entry={open} />],
                  ['On', <Target key="on" entry={open} />],
                ]}
              />
              <section>
                <h3>What changed</h3>
                <Changes changes={diffFields(open.before, open.after)} />
              </section>
            </Dialog>
          )}

          <div className="row">
            <Button
              disabled={history.length === 1}
              onClick={() => {
                setHistory((current) => current.slice(0, -1));
              }}
            >
              Previous
            </Button>
            <Button
              disabled={entries.data.nextCursor === null}
              onClick={() => {
                const next = entries.data.nextCursor;
                if (next !== null) setHistory((current) => [...current, next]);
              }}
            >
              Next
            </Button>
            <span className="muted text-sm">
              Page {history.length}
              {entries.isFetching ? ' · loading…' : ''}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function Actor({ entry }: { entry: AuditLogEntry }): ReactNode {
  if (entry.actorType === 'system') return <span className="muted">Bartizo</span>;
  if (entry.actorType === 'agent' && entry.actorId !== null) {
    return (
      <>
        {/* A server that has gone has no page; its id is what is left of it. */}
        {entry.actorName === null ? (
          <code>{entry.actorId}</code>
        ) : (
          <Link to={`/servers/${encodeURIComponent(entry.actorId)}`}>{entry.actorName}</Link>
        )}
        <span className="cell-sub">its agent</span>
      </>
    );
  }
  if (entry.actorName !== null) return entry.actorName;
  // An account that has since gone keeps its id, which is all the entry has.
  return entry.actorId === null ? (
    <span className="muted">unknown</span>
  ) : (
    <code>{entry.actorId}</code>
  );
}

function Target({ entry }: { entry: AuditLogEntry }): ReactNode {
  if (entry.target === null) return <span className="muted">—</span>;
  if (entry.targetName !== null) {
    return <Link to={`/servers/${encodeURIComponent(entry.target)}`}>{entry.targetName}</Link>;
  }
  return (
    <Link
      to={`/audit-log?target=${encodeURIComponent(entry.target)}`}
      title="Everything done to it"
    >
      <code>{entry.target}</code>
    </Link>
  );
}

function Changes({ changes }: { changes: FieldChange[] }): ReactNode {
  if (changes.length === 0) return null;
  return (
    <dl className="details">
      {changes.map((change) => (
        <Fragment key={change.path}>
          <dt>{change.path === '' ? 'value' : change.path}</dt>
          <dd className="text-sm">
            {change.kind === 'added' ? (
              <>set to {describeValue(change.after)}</>
            ) : change.kind === 'removed' ? (
              <>no longer set (was {describeValue(change.before)})</>
            ) : (
              <>
                {describeValue(change.before)} → {describeValue(change.after)}
              </>
            )}
          </dd>
        </Fragment>
      ))}
    </dl>
  );
}
